/* eslint-disable no-nested-ternary */
import React from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {
  AddToTrolley,
  AddToWishlist,
  SpecCardSkeleton,
  ProductCardSkeleton,
  ErrorBoundary,
  SpecCard
} from '@sainsburys-tech/bolt'
import { TuLogo } from '@sainsburys-tech/bolt-icons'
import { trackWishlist } from '../../helpers/TaggingSerializer'
import { contentAreaTypes } from '../../config'
import Templates from './ProductList.template'
import CategoryListSkeleton from '../CategoryList/CategoryListSkeleton'
import * as Styled from './styles.js'

const CMS = loadable.lib(() => import(/* webpackChunkName: "cms-components" */ '@sainsburys-tech/bolt-cms-components'))

class ProductList extends React.Component {
  shouldComponentUpdate(nextProps) {
    if (nextProps.loader.show !== this.props.loader.show) return true
    if (nextProps.templateType !== this.props.templateType) return true
    if (nextProps.pageType !== this.props.pageType) return true
    if (JSON.stringify(nextProps.prodsList) !== JSON.stringify(this.props.prodsList)) return true
    return false
  }

  getProductCardLayout(templateType, pageType) {
    if (pageType === Templates.pageTypeBrowse) {
      if (templateType === Templates.fashionTemplate) {
        return Templates.clothingLayout
      }
      if (templateType === Templates.whiteGoodTemplate) {
        return Templates.whiteGoodTemplate
      }
    }
    return Templates.defaultTemplate
  }

  getBrandLogo(layout, pageType, brand) {
    let logo = null
    if (layout === Templates.clothingLayout) {
      logo = brand === 'Tu' ? <TuLogo height={24} width={24} /> : brand
    } else if (pageType === 'browse' && brand === 'Habitat') {
      logo = <Styled.HabitatLogo height={18} width={60} />
    }
    return logo
  }

  getProductCardClass(isVariant) {
    return isVariant ? 'xs-6--none sm-4' : 'xs-block xs-auto--none sm-6 md-4'
  }

  showSkeleton(loader, attributes) {
    return loader.show || typeof attributes.price !== 'number'
  }

  isVariantProductCard(pageType, searchCookie) {
    return pageType === Templates.pageTypeBrowse || (searchCookie === '2' ? Templates.pageTypeSearch : false)
  }

  alternativeImagesConditionChecker(templateType, pageType) {
    return (
      templateType && templateType.toLowerCase() !== Templates.defaultTemplate && pageType === Templates.pageTypeBrowse
    )
  }

  populateSpecificationData(attributes) {
    return {
      heading: Templates.specificationHeader,
      secondaryData: Templates.secondaryDataArray(attributes),
      headerLink: (
        <>
          <a href={`/product/${attributes.partNumber}`}>{Templates.headerText}</a>
        </>
      ),
      overviewData: { content: attributes.detailAttributes },
      smallTitle: true
    }
  }

  isExcluded(isDeliverable, isReservable, hasVariations) {
    return !isDeliverable && !isReservable && !hasVariations
  }

  buildQueryParamTracking(partNumber, searchTerm, currentPage, pageSize, index, recall, relevancyRank) {
    const position = pageSize * (currentPage - 1) + index
    return `/product/${partNumber}?clickSR=slp:term:${searchTerm}:${position}:${recall}:${relevancyRank}`
  }

  render() {
    const {
      loader,
      templateType,
      pageType,
      destinationPageType,
      searchTerm,
      numberOfResults,
      currentPage,
      pageSize,
      prodsList,
      relavancyRecallOffset
    } = this.props

    const theme = process.env.BRAND || window.BRAND
    const layout = this.getProductCardLayout(templateType, pageType)
    const imageVariant = this.alternativeImagesConditionChecker(templateType, pageType)
    const whiteGoodCategory = layout === Templates.whiteGoodTemplate

    const productCardClass = 'xs-6--none sm-4'

    const handleAddToTrolleyClick = (event, partNumber, productInfo, positionInList, clickOrigin) =>
      AddToTrolley.onAddToTrolleyModalOpen(productInfo)(clickOrigin, positionInList)()
    const className = whiteGoodCategory ? 'xs-block xs-12--none md-12' : productCardClass
    let recallPosition = relavancyRecallOffset || 0
    const Skeleton = () => (
      <Styled.SkeletonWrapper className={className}>
        <ProductCardSkeleton isPortrait={layout === Templates.clothingLayout} />
      </Styled.SkeletonWrapper>
    )
    const SpecSkeleton = () => (
      <Styled.SkeletonWrapper whiteGoods className={className}>
        <SpecCardSkeleton />
      </Styled.SkeletonWrapper>
    )
    const ProductCard = loadable(() => import(/* webpackChunkName: "labs-product-card" */ './StyledProductCard.js'), {
      fallback: <Skeleton />
    })

    return (
      <ErrorBoundary>
        <Styled.ProductList data-test='product-list'>
          {loader.show && destinationPageType === 'CLP' ? (
            <CategoryListSkeleton isFullSize />
          ) : (
            prodsList.map((product, index) => {
              if (product.type === contentAreaTypes.ContentInList)
                return !loader.show ? (
                  <ErrorBoundary>
                    <CMS>
                      {({ components }) => {
                        const { M053 } = components
                        return (
                          <ProductCard
                            as='div'
                            isM053
                            className={className}
                            data-test='product-list-m053'
                            key={`M053-${index}`}>
                            <M053 heading={product.heading} image={product.image} link={product.link} />
                          </ProductCard>
                        )
                      }}
                    </CMS>
                  </ErrorBoundary>
                ) : !whiteGoodCategory ? (
                  <Skeleton />
                ) : (
                  <SpecSkeleton />
                )
              const partNumber = product.id
              const attributes = { ...product.attributes, partNumber }

              const trolleyOnClick = (event, partNumber) => {
                const productInfo = [{ id: partNumber, price: attributes.price.toString(), quantity: 1 }]
                const clickOrigin =
                  pageType === Templates.pageTypeBrowse
                    ? Templates.browseAddToTrolleyOrigin
                    : Templates.searchAddToTrolleyOrigin
                handleAddToTrolleyClick(event, partNumber, productInfo, index + 1, clickOrigin)
                this.props.sendHookLogicClickBeacon(attributes, this.props.hookLogic, partNumber)
              }
              const productInfoOnly = this.isExcluded(
                attributes.deliverable,
                attributes.reservable,
                attributes.hasVariations
              )
              this.props.sendHookLogicImpressionBeacon(attributes, this.props.hookLogic)
              const userState =
                this.props.userDetails && this.props.userDetails.data ? this.props.userDetails.data.userState : false
              const colorVariants = attributes.variants && attributes.variants.Colour

              recallPosition = !attributes.featured ? (recallPosition += 1) : recallPosition

              return (
                <ErrorBoundary key={`${partNumber}-${index}-boundary`}>
                  {!whiteGoodCategory ? ( // eslint-disable-line
                    !this.showSkeleton(loader, attributes) ? (
                      <ProductCard
                        key={`${partNumber}${index}`}
                        isPortrait={layout === Templates.clothingLayout}
                        partNumber={partNumber}
                        imageHoverActive={imageVariant}
                        colorVariants={!!colorVariants && colorVariants.length > 1 && colorVariants}
                        brand={this.getBrandLogo(layout, pageType, attributes.brand)}
                        brandTheme={theme}
                        featured={attributes.featured} // TODO: FEATURED
                        name={attributes.name}
                        price={attributes.price}
                        wasPrice={!!attributes.wasPrice && attributes.wasPrice > 0 && attributes.wasPrice}
                        specialOfferText={attributes.specialOfferText}
                        rating={!!attributes.avgRating && attributes.avgRating > 0 && attributes.avgRating}
                        reviewsCount={attributes.reviewsCount}
                        productLinkOverride={
                          ['search', 'directory'].includes(pageType) && !attributes.productLink
                            ? this.buildQueryParamTracking(
                                partNumber,
                                searchTerm,
                                currentPage,
                                pageSize,
                                recallPosition,
                                numberOfResults,
                                attributes.relevancyRank
                              )
                            : attributes.productLink
                        }
                        badge={attributes.badge}
                        freeDelivery={attributes.freeDelivery}
                        hasVariations={attributes.hasVariations}
                        hasMultiplePrices={attributes.hasMultiplePrices}
                        productInfoOnly={productInfoOnly}
                        buttonLabel={attributes.hasVariations && (productInfoOnly ? 'More Details' : 'Choose Options')}
                        cta={
                          <ErrorBoundary>
                            <AddToTrolley.Button
                              className='btn-cta'
                              partNumber={partNumber}
                              openView
                              showIcon
                              onClick={trolleyOnClick}
                            />
                          </ErrorBoundary>
                        }
                        wishlistCta={
                          theme !== 'habitat' && (
                            <ErrorBoundary>
                              <AddToWishlist.Button
                                item={{
                                  partNumber,
                                  hasVariant: attributes.hasVariations
                                }}
                                tooltipDurationToShow={10000}
                                tooltipWidth={260}
                                onUpdate={isChecked => trackWishlist(isChecked, partNumber, userState)}
                                tooltipPosition='right'
                              />
                            </ErrorBoundary>
                          )
                        }
                        className={className}
                      />
                    ) : (
                      <Skeleton />
                    )
                  ) : !this.showSkeleton(loader, attributes) ? (
                    <Styled.WhiteGoodsWrapper className={className}>
                      <SpecCard
                        key={`${partNumber}${index}`}
                        isPortrait={layout === Templates.clothingLayout}
                        partNumber={partNumber}
                        imageHoverActive={this.imageVariant}
                        colorVariants={!!colorVariants && colorVariants.length > 1 && colorVariants}
                        brand={this.getBrandLogo(layout, pageType, attributes.brand)}
                        featured={attributes.featured} // TODO: FEATURED
                        name={attributes.name}
                        price={attributes.price}
                        wasPrice={!!attributes.wasPrice && attributes.wasPrice > 0 && attributes.wasPrice}
                        specialOfferText={attributes.specialOfferText}
                        rating={!!attributes.avgRating && attributes.avgRating > 0 && attributes.avgRating}
                        reviewsCount={attributes.reviewsCount}
                        productLinkOverride={attributes.productLink}
                        badge={attributes.badge}
                        freeDelivery={attributes.freeDelivery}
                        hasVariations={attributes.hasVariations}
                        hasMultiplePrices={attributes.hasMultiplePrices}
                        productInfoOnly={productInfoOnly}
                        buttonLabel={productInfoOnly ? 'More Details' : 'Choose Options'}
                        cta={
                          <ErrorBoundary>
                            <AddToTrolley.Button className='btn-cta' partNumber={partNumber} openView showIcon />
                          </ErrorBoundary>
                        }
                        wishlistCta={
                          <ErrorBoundary>
                            <AddToWishlist.Button
                              item={{
                                partNumber,
                                hasVariant: attributes.hasVariations
                              }}
                              tooltipDurationToShow={10000}
                              tooltipWidth={260}
                              onUpdate={isChecked => trackWishlist(isChecked, partNumber, userState)}
                              tooltipPosition='left'
                            />
                          </ErrorBoundary>
                        }
                        specificationData={this.populateSpecificationData(attributes)}
                      />
                    </Styled.WhiteGoodsWrapper>
                  ) : (
                    <SpecSkeleton />
                  )}
                </ErrorBoundary>
              )
            })
          )}
        </Styled.ProductList>
      </ErrorBoundary>
    )
  }
}

ProductList.propTypes = {
  prodsList: PropTypes.array.isRequired,
  pageType: PropTypes.string,
  templateType: PropTypes.string,
  hookLogic: PropTypes.object.isRequired,
  sendHookLogicImpressionBeacon: PropTypes.func.isRequired,
  userDetails: PropTypes.object,
  destinationPageType: PropTypes.string,
  searchTerm: PropTypes.string,
  numberOfResults: PropTypes.number,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  relavancyRecallOffset: PropTypes.number,
  loader: PropTypes.shape({
    show: PropTypes.bool
  }),
  sendHookLogicClickBeacon: PropTypes.func
}

ProductList.defaultProps = {
  searchTerm: '',
  numberOfResults: null,
  currentPage: 1,
  pageSize: 30,
  destinationPageType: '',
  pageType: 'search',
  templateType: '',
  userDetails: {},
  relavancyRecallOffset: 0,
  loader: {
    show: false
  },
  sendHookLogicClickBeacon: () => {}
}

export default ProductList
